import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '../lib/utils';
import { LAYOUT } from '../styles/layout-tokens';

// MF-228 — Shared horizontally-scrolling tab strip for the Settings/Admin
// shells. On narrow/touch viewports the strip overflows; gradient edge-fades
// signal that more tabs lie off-screen (cut-off tabs alone are easy to miss).
// Fade colour matches the shell header bg (white / zinc-900).
export function ScrollableTabStrip({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const left = scrollLeft > 1;
    const right = scrollLeft + clientWidth < scrollWidth - 1;
    // Bail out when nothing changed so the every-render effect can't loop.
    setEdges((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      el.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [update]);

  // Recompute after every render so the fades react to tab lists that change
  // post-mount (e.g. flag-gated Settings tabs that appear once settings load).
  useEffect(() => {
    update();
  });

  return (
    <div className={cn(LAYOUT.container.wide, 'mx-auto', 'relative')}>
      <nav
        ref={ref}
        className={cn(LAYOUT.page.paddingX, 'flex gap-1 -mb-px overflow-x-auto whitespace-nowrap')}
      >
        {children}
      </nav>
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 w-8 bg-linear-to-r from-white to-transparent transition-opacity dark:from-zinc-900',
          edges.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 w-8 bg-linear-to-l from-white to-transparent transition-opacity dark:from-zinc-900',
          edges.right ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
}
